import { RouteRecordRaw } from 'vue-router'
import { COUNTRY_ALIAS, getPayrollValidCountries, isNominaIcpRegisterCountry } from '@/micro/modules/version'
import { ApplicationVersion } from '@/micro/types/version.type'
import { APP_ENVIRONMENT } from '@/micro/utils/environment'
import { isIcpRegisterCountry } from '@/micro/utils/icpAuthNavigation'

const resolveRegisterCountryParam = (countryParam: string): ApplicationVersion =>
  (COUNTRY_ALIAS[countryParam] ?? countryParam) as ApplicationVersion

const registerRoutes: Array<RouteRecordRaw> = [
  {
    path: '/user/register',
    name: 'register',
    component: () => import('@/micro/views/register/RegisterView.vue'),
  },
  {
    path: '/user/register/pos',
    name: 'register-pos',
    component: () => import('@/micro/views/register/RegisterView.vue'),
  },
  {
    path: '/user/register/tpv',
    name: 'register-tpv',
    component: () => import('@/micro/views/register/RegisterView.vue'),
  },
  {
    path: '/user/register/punto-venta',
    name: 'register-pv',
    component: () => import('@/micro/views/register/RegisterView.vue'),
  },
  {
    path: '/user/register/nomina',
    name: 'register-payroll',
    component: () => import('@/micro/views/register/RegisterView.vue'),
  },
  {
    path: '/user/register/country/costaRica',
    name: 'register-costa-rica',
    component: () => import('@/micro/views/register-icp/shared/RegisterView.vue'),
    meta: { icpCountry: 'costaRica' as ApplicationVersion },
  },
  {
    path: '/user/register/country/costa-rica',
    redirect: to => ({
      path: '/user/register/country/costaRica',
      query: to.query,
    }),
  },
  {
    path: '/user/register/country/costarica',
    redirect: to => ({
      path: '/user/register/country/costaRica',
      query: to.query,
    }),
  },
  {
    path: '/user/register/country/colombia',
    name: 'register-colombia',
    component: () => import('@/micro/views/register-icp/shared/RegisterView.vue'),
    meta: { icpCountry: 'colombia' as ApplicationVersion },
  },
  {
    path: '/user/register/country/mexico',
    name: 'register-mexico',
    component: () => import('@/micro/views/register-icp/shared/RegisterView.vue'),
    meta: { icpCountry: 'mexico' as ApplicationVersion },
  },
  {
    path: '/user/register/country/republicaDominicana',
    name: 'register-republica-dominicana',
    component: () => import('@/micro/views/register-icp/shared/RegisterView.vue'),
    meta: { icpCountry: 'republicaDominicana' as ApplicationVersion },
  },
  {
    path: '/user/register/country/republica-dominicana',
    redirect: to => ({
      path: '/user/register/country/republicaDominicana',
      query: to.query,
    }),
  },
  {
    path: '/user/register/country/republicadominicana',
    redirect: to => ({
      path: '/user/register/country/republicaDominicana',
      query: to.query,
    }),
  },
  {
    path: '/user/register/country/rdominicana',
    redirect: to => ({
      path: '/user/register/country/republicaDominicana',
      query: to.query,
    }),
  },
  {
    path: '/user/register/country/:country',
    name: 'register-by-country',
    component: () => import('@/micro/views/register/RegisterView.vue'),
    beforeEnter: (to, _from, next) => {
      const country = resolveRegisterCountryParam(String(to.params.country))

      if (isIcpRegisterCountry(country)) {
        const routeNames: Partial<Record<ApplicationVersion, string>> = {
          costaRica: 'register-costa-rica',
          colombia: 'register-colombia',
          mexico: 'register-mexico',
          republicaDominicana: 'register-republica-dominicana',
        }

        next({
          name: routeNames[country] || 'register-costa-rica',
          query: to.query,
          replace: true,
        })
        return
      }

      if (country !== to.params.country) {
        next({
          name: 'register-by-country',
          params: { country },
          query: to.query,
          replace: true,
        })
        return
      }

      next()
    },
  },
  {
    path: '/user/register/nomina/country/:country',
    name: 'register-payroll-by-country',
    component: () => import('@/micro/views/register/PayrollCountryRegisterView.vue'),
    beforeEnter: (to, _from, next) => {
      const country = resolveRegisterCountryParam(String(to.params.country))
      const validCountries = getPayrollValidCountries(APP_ENVIRONMENT)

      if (!validCountries.includes(country)) {
        next({
          name: 'register-payroll-by-country',
          params: { country: 'colombia' },
          replace: true,
        })
        return
      }

      if (country !== to.params.country) {
        next({
          name: 'register-payroll-by-country',
          params: { country },
          query: to.query,
          replace: true,
        })
        return
      }

      if (isNominaIcpRegisterCountry(country)) {
        to.meta.icpCountry = country
      }

      next()
    },
  },
  {
    path: '/user/register/home/country/:country',
    name: 'register-home-by-country',
    redirect: to => {
      const country = resolveRegisterCountryParam(String(to.params.country))

      if (isIcpRegisterCountry(country)) {
        const routeNames: Partial<Record<ApplicationVersion, string>> = {
          costaRica: 'register-costa-rica',
          colombia: 'register-colombia',
          mexico: 'register-mexico',
          republicaDominicana: 'register-republica-dominicana',
        }

        return {
          name: routeNames[country] || 'register-costa-rica',
          query: to.query,
        }
      }

      return {
        name: 'register-by-country',
        params: { country },
        query: to.query,
      }
    },
  },
  {
    path: '/user/register/alianza/educacion/:alliance',
    name: 'register-by-education-alliance',
    component: () => import('@/micro/views/education-alliance-register/EducationAllianceRegisterView.vue'),
  },
  // {
  //   path: '/user/register/exp1',
  //   name: 'register-col-exp1',
  //   component: () => import('@/micro/views/experiments/exp1/RegisterView.vue'),
  // },
  // {
  //   path: '/user/register/exp2',
  //   name: 'register-dom-exp2',
  //   component: () => import('@/micro/views/experiments/exp2/RegisterView.vue'),
  // },
  // {
  //   path: '/user/register/exp3',
  //   name: 'register-cri-exp3',
  //   component: () => import('@/micro/views/experiments/exp3/RegisterView.vue'),
  // },
]

export default registerRoutes
